import { useApolloClient } from '@apollo/client';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type ToastProps from '@aurora/shared-client/components/common/ToastAlert/ToastAlertProps';
import useFrameEnd from '@aurora/shared-client/components/context/AnalyticsParentFrames/useFrameEnd';
import ParentFrameSupplier from '@aurora/shared-utils/analytics/ParentFrameSupplier';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useToasts from '@aurora/shared-client/components/context/ToastContext/useToasts';
import useMessagePolicies from '@aurora/shared-client/components/messages/useMessagePolicies';
import useImperativeQueryWithTracing from '@aurora/shared-client/components/useImperativeQueryWithTracing';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import Icons from '@aurora/shared-client/icons';
import type {
  AcceptedSolutionMessage,
  Conversation,
  Message,
  MessageConnection,
  MessageEdge,
  ReplyMessage
} from '@aurora/shared-generated/types/graphql-schema-types';
import { RepliesFormat } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import IdConverter from '@aurora/shared-utils/graphql/IdConverter/IdConverter';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { getLog } from '@aurora/shared-utils/log';
import React, { useContext } from 'react';
import { Badge, useClassNameMapper } from 'react-bootstrap';
import {
  updateSolutionMessageCache,
  updateSolutionsListCache
} from '../../../helpers/messages/MessageHelper/MessageHelper';
import type {
  AcceptedSolutionButtonFragment,
  ContextMessageQuery,
  ContextMessageQueryVariables,
  MarkMessageAsSolutionMutation,
  UnmarkMessageAsSolutionMutation
} from '../../../types/graphql-types';
import contextMessageQuery from '../../context/ContextMessage.query.graphql';
import { UrlObject, useContextObjectRefFromUrl } from '../../context/useContextObjectFromUrl';
import useTranslation from '../../useTranslation';
import {
  deepLinkMessageVar,
  updatedLocalStateForNewMessageVar,
  updatedMessageForDeepLinkVar
} from '../MessageDeepLink/MessageReactVarHelper';
import localStyles from './AcceptedSolutionButton.module.css';
import markSolutionMutation from './MarkMessageAsSolution.mutation.graphql';
import unmarkSolutionMutation from './UnmarkMessageAsSolution.mutation.graphql';

const log = getLog(module);

interface Props {
  /**
   * The message.
   */
  message: AcceptedSolutionButtonFragment;
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * Callback to update the messages in cache/local memory
   * @param message
   */
  onUpdate?: (message: Message) => void;
  /**
   * RepliesFormat of the discussion style.
   * Can be either threaded or linear.
   */
  repliesFormat?: RepliesFormat;
}

/**
 * If the edit button will be shown
 *
 * @param message - Message to render the button for.
 * @param canAcceptSolution - Permissions for the current user if they can view button.
 * @param canRejectSolution - Permissions for the current user if they can view button.
 */
function shouldRenderEditButton(
  message: AcceptedSolutionButtonFragment,
  canAcceptSolution: boolean,
  canRejectSolution: boolean
): boolean {
  return (
    (canAcceptSolution && message?.conversation?.solved === false) ||
    (canRejectSolution && (message as AcceptedSolutionMessage)?.solution)
  );
}

/**
 * If the button will be shown
 *
 * @param message - Message to render the button for.
 */
function shouldRenderButton(message: AcceptedSolutionButtonFragment): boolean {
  return (
    (message as AcceptedSolutionMessage).solution ||
    (message.depth === 0 && message.conversation.solved)
  );
}
/**
 * A button allowing the end user to mark or unmark a message as an accepted solution.
 * @constructor
 *
 * @author Dolan Halbrook
 */
const AcceptedSolutionButton: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  message,
  onUpdate,
  repliesFormat = RepliesFormat.Threaded
}) => {
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.ACCEPTED_SOLUTION_BUTTON
  );
  const cx = useClassNameMapper(localStyles);
  const { addToast } = useToasts();
  const tenant = useContext(TenantContext);
  const { isAnonymous } = useRegistrationStatus();
  const isRootMessage = message.depth === 0;
  const client = useApolloClient();
  const { id: replyId } = useContextObjectRefFromUrl(UrlObject.REPLY);
  const isPermalinkReply = replyId !== null;

  const [markMessage] = useMutationWithTracing<MarkMessageAsSolutionMutation>(
    module,
    markSolutionMutation
  );
  const [unmarkMessage] = useMutationWithTracing<UnmarkMessageAsSolutionMutation>(
    module,
    unmarkSolutionMutation
  );
  const [frameEnd] = useFrameEnd();

  const { id, solution } = message as AcceptedSolutionMessage;

  // useCache is set to false on this component to get the
  // latest message policy values from network once any action is performed.
  const { data: policiesData, loading: policiesLoading } = useMessagePolicies(
    module,
    {
      id: message.id,
      useCanAcceptSolution: true,
      useCanRejectSolution: true
    },
    isAnonymous || IdConverter.isOptimistic(message?.id),
    false
  );

  const refetchMessage = useImperativeQueryWithTracing<
    ContextMessageQuery,
    ContextMessageQueryVariables
  >(module, contextMessageQuery);

  if (textLoading || !message || policiesLoading) {
    return null;
  }

  /**
   * Helper to render error toasts.
   * @param toastVariant toast variant.
   * @param alertVariant alert variant.
   * @param toastTitle toast title.
   * @param toastMessage toast message.
   * @param autoHide either to auto hide or not.
   * @param toastId the toast id.
   */
  function renderToast(
    toastVariant: ToastVariant,
    alertVariant: ToastAlertVariant,
    toastTitle: string,
    toastMessage: string,
    autoHide: boolean,
    toastId: string
  ) {
    const toastProps: ToastProps = {
      toastVariant: toastVariant,
      alertVariant: alertVariant,
      title: toastTitle,
      message: toastMessage,
      autohide: autoHide,
      id: toastId
    };
    addToast(toastProps);
  }

  /**
   * Renders error toasts.
   * @param textKey the text stating the failure operation.
   * @param errorId the message id.
   * @param errors mutation errors.
   */
  function renderError(textKey: string, errorId: string, errors): void {
    const formattedErrorId = `message-${errorId}-accepted-solution-${textKey}`;
    const errorTypeName = errors[0]?.__typename;
    if (
      errorTypeName === 'TopicAlreadySolvedError' ||
      errorTypeName === 'MessageAlreadySolvedError'
    ) {
      renderToast(
        ToastVariant.FLYOUT,
        ToastAlertVariant.WARNING,
        formatMessage('topicAlreadySolvedErrorTitle'),
        formatMessage('topicAlreadySolvedErrorDesc'),
        true,
        formattedErrorId
      );
      return;
    }
    renderToast(
      ToastVariant.BANNER,
      ToastAlertVariant.DANGER,
      formatMessage('errorHeader'),
      formatMessage(textKey),
      true,
      formattedErrorId
    );
  }

  /**
   * Updates new replies on the permalink page.
   * @param isSolved message's current solved status.
   * @param conversation conversation the message is of.
   * @param replies replies messages that needs to be updated.
   * returns updated replies.
   */
  function updateReplies(
    isSolved: boolean,
    conversation: Conversation,
    replies: MessageConnection
  ) {
    if (!replies || replies?.edges.length === 0) {
      return;
    }
    const updatedEdges = [];
    replies.edges.forEach(reply => {
      const replyMessage = (reply as MessageEdge).node as ReplyMessage;
      const updatedReplies = updateReplies(isSolved, conversation, replyMessage.replies);
      updatedEdges.push({
        ...replyMessage,
        conversation: conversation,
        solution: message.id === replyMessage.id && isSolved,
        replies: updatedReplies
      });
    });
    replies.edges.forEach((reply, index) => ((reply as MessageEdge).node = updatedEdges[index]));
    return replies;
  }

  /**
   * Updates the  permalink reply's parent.
   * @param isSolved message's current solved status.
   * @param conversation conversation the message is of.
   * @param parentMessage the parent message that needs to be updated.
   * returns permalink reply's update parent message.
   */
  function updateParentMessage(
    isSolved: boolean,
    conversation: Conversation,
    parentMessage: Message
  ) {
    if (!parentMessage) {
      return;
    } else if (parentMessage.depth === 0) {
      return parentMessage;
    }
    const updatedParent = updateParentMessage(
      isSolved,
      conversation,
      (parentMessage as ReplyMessage).parent
    );
    const updatedReplies = updateReplies(isSolved, conversation, parentMessage.replies);
    return {
      ...parentMessage,
      conversation: conversation,
      solution: message.id === parentMessage.id && isSolved,
      parent: updatedParent,
      ...(parentMessage.replies && { replies: updatedReplies })
    };
  }

  /**
   * Updates the local state for permalink reply using reactive var.
   * @param isSolved message's current solved status.
   * @param conversation conversation the message is of.
   */
  function updateDeepLinkVar(isSolved: boolean, conversation: Conversation): void {
    const deepLinkMessageInStore = deepLinkMessageVar();
    const parentMessage = updateParentMessage(
      isSolved,
      conversation,
      (deepLinkMessageInStore as ReplyMessage)?.parent
    );
    //update replies for permalink message
    const updatedReplies = updateReplies(
      isSolved,
      conversation,
      (deepLinkMessageInStore as ReplyMessage)?.replies
    );
    if (onUpdate || deepLinkMessageInStore.id !== message.id) {
      updatedMessageForDeepLinkVar({
        ...deepLinkMessageInStore,
        parent: parentMessage,
        ...(updatedReplies && { replies: updatedReplies }),
        conversation
      } as unknown as Message);
    } else {
      updatedMessageForDeepLinkVar({
        ...message,
        parent: parentMessage,
        ...(updatedReplies && { replies: updatedReplies }),
        solution: isSolved,
        conversation
      } as unknown as Message);
    }
  }

  /**
   * Updates the cached reply's solution field.
   * As it is not sure which type of reply (ForumReplyMessage or AcceptedSolutionMessage)
   * is present in cache at a time as multiple components on UI may have its own type
   * for eg; MessageSolutionList have AcceptedSolutionMessage type
   * MessageReplies have ForumReplyMessage if not solved
   * RecentContentWidget have ForumReplyMessage, it will update the both the types.
   */
  function updateCacheForMessage(markSolved: boolean): void {
    client.cache.modify({
      id: `ForumReplyMessage:${id}`,
      fields: {
        solution() {
          return markSolved;
        }
      }
    });
    client.cache.modify({
      id: `AcceptedSolutionMessage:${id}`,
      fields: {
        solution() {
          return markSolved;
        }
      }
    });
  }

  const canAcceptSolution = checkPolicy(policiesData?.message?.messagePolicies?.canAcceptSolution);
  const canRejectSolution = checkPolicy(policiesData?.message?.messagePolicies?.canRejectSolution);

  async function handleEvent(): Promise<void> {
    const parentFrameId = ParentFrameSupplier.getParentFrameId();
    if (!solution && canAcceptSolution) {
      log.info('Marking message %s as solution', id);

      const { data, errors } = await markMessage({
        variables: { messageId: id },
        context: { parentFrameId },
        onCompleted: (d): void => {
          if (d.markMessageAsSolution?.errors === null) {
            // This method is used to update the cache objects. (will not work for new messages in local state).
            updateSolutionsListCache(tenant, client.cache, message, true, repliesFormat);
            updateSolutionMessageCache(client.cache, message, true);
            updateCacheForMessage(true);
            const conversation = { ...message.conversation, solved: true };
            // This block is used for handling new messages use case.
            if (onUpdate) {
              // if the message view which is calling is rendering this component from threadedreplylist
              // callback will be available to udpate the new messages local state.
              if (isPermalinkReply) {
                updateDeepLinkVar(true, conversation as Conversation);
              }
              onUpdate({
                ...message,
                solution: true,
                conversation
              } as unknown as Message);
            } else {
              // On Update callback will not be available when this component is used in message view
              // while rendering message solution list on topic message,
              // hence using this react var to update the message with latest.
              // The listener is present on message view so it will re render with latest info.
              // Please reset the value to null once we capture the latest info on message view
              // otherwise it may result in unnecesary renders as the listener is on all replies
              if (isPermalinkReply) {
                updateDeepLinkVar(true, conversation as Conversation);
              } else {
                updatedLocalStateForNewMessageVar({
                  ...message,
                  solution: true,
                  conversation
                } as unknown as Message);
              }
            }
            frameEnd({
              context: { parentFrameId }
            });
          }
        }
      });
      if (errors || data?.markMessageAsSolution?.errors) {
        log.error('Unable to mark message as solution: %O', errors);
        renderError('errorAdd', id, data?.markMessageAsSolution?.errors);
      } else {
        await refetchMessage({
          id: id,
          useHoverCard: false
        });
      }
    } else if (solution && canRejectSolution) {
      log.info('Unmarking message %s as solution', id);

      const { data, errors } = await unmarkMessage({
        variables: { messageId: id },
        context: { parentFrameId },
        onCompleted: (d): void => {
          if (d.unmarkMessageAsSolution?.errors === null) {
            updateSolutionsListCache(tenant, client.cache, message, false, repliesFormat);
            updateSolutionMessageCache(client.cache, message, false);
            // This method is used to update the cache objects. (will not work for new messages in local state).
            updateCacheForMessage(false);
            const conversation = { ...message.conversation, solved: false };
            // This block is used for handling new messages use case.
            if (onUpdate) {
              // if the message view which is calling is rendering this component from threadedreplylist
              // callback will be available to udpate the new messages local state.
              if (isPermalinkReply) {
                updateDeepLinkVar(false, conversation as Conversation);
              }
              onUpdate({
                ...message,
                solution: false,
                conversation
              } as unknown as Message);
            } else {
              // On Update callback will not be available when this component is used in message view
              // while rendering message solution list on topic message,
              // hence using this react var to update the message with latest.
              // The listener is present on message view so it will re render with latest info.
              // Please reset the value to null once we capture the latest info on message view
              // otherwise it may result in unnecesary renders as the listener is on all replies
              if (isPermalinkReply) {
                updateDeepLinkVar(false, conversation as Conversation);
              } else {
                updatedLocalStateForNewMessageVar({
                  ...message,
                  solution: false,
                  conversation
                } as unknown as Message);
              }
            }
            frameEnd({
              context: { parentFrameId }
            });
          }
        }
      });
      if (errors || data?.unmarkMessageAsSolution?.errors) {
        log.error('Unable to unmark message as solution: %O', errors);
        // rollbacks.forEach(rollback => rollback());
        renderError('errorRemove', id, data?.unmarkMessageAsSolution?.errors);
      } else {
        await refetchMessage({
          id: id,
          useHoverCard: false
        });
      }
    }
  }

  if (shouldRenderEditButton(message, canAcceptSolution, canRejectSolution)) {
    return (
      <Button
        variant={ButtonVariant.LINK}
        className={cx(className, 'lia-g-action-btn lia-solution-btn', {
          'lia-is-solution': solution
        })}
        onClick={handleEvent}
      >
        <span className={cx('lia-solution-text')}>
          {solution ? formatMessage('accepted') : formatMessage('accept')}
        </span>
        {solution && (
          <Icon icon={Icons.CloseIcon} className={cx('lia-close-icon')} size={IconSize.FROM_CSS} />
        )}
      </Button>
    );
  } else if (shouldRenderButton(message) && !isRootMessage) {
    return (
      <Button
        variant={ButtonVariant.LINK}
        disabled
        className={cx(className, 'lia-g-action-btn lia-solution-btn lia-is-solution')}
      >
        <span className={cx('lia-solution-text')}>{formatMessage('accepted')}</span>
      </Button>
    );
  } else if (shouldRenderButton(message) && isRootMessage) {
    return (
      <Badge pill className={cx('lia-solved-badge', className)}>
        {formatMessage('solved')}
      </Badge>
    );
  }
  return null;
};

export { shouldRenderEditButton, shouldRenderButton };
export default AcceptedSolutionButton;
